import { useEffect, useState } from "react";
import { useCompanyViewModel } from "../../viewmodels/company/useCompanyViewModel";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import GoogleMapPickerScript from "../../components/common/GoogleMapPickerScript";

const EditCompany = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const {
    getCompanyById,
    updateCompany,
    companyTypes,
    setTypeSearch,
    menus,
  } = useCompanyViewModel();

  const [form, setForm] = useState<any>(null);

  // ================= LOAD DATA =================
  useEffect(() => {
    if (!id) return;

    const load = async () => {
      const data = await getCompanyById(id);

      setForm({
        ...data,

        //  COMPANY TYPES
        companyTypeIds:
          data.companyCategories?.map((c: any) => c.companyTypeId) || [],

        //  FIXED MENU MAPPING (IMPORTANT)
        menuIds:
          data.menus?.flatMap((m: any) => [
            m.id,
            ...(m.children?.map((c: any) => c.id) || []),
          ]) || [],

        //  PUBLIC
        isPublic: data.isPublic || false,
      });
    };

    load();
  }, [id]);

  // ================= HELPERS =================
  const updateForm = (key: string, value: any) => {
    setForm((prev: any) => ({
      ...prev,
      [key]: value,
    }));
  };

  const toggleType = (id: number) => {
    setForm((prev: any) => ({
      ...prev,
      companyTypeIds: prev.companyTypeIds.includes(id)
        ? prev.companyTypeIds.filter((x: number) => x !== id)
        : [...prev.companyTypeIds, id],
    }));
  };

  const toggleMenu = (id: string) => {
    setForm((prev: any) => ({
      ...prev,
      menuIds: prev.menuIds.includes(id)
        ? prev.menuIds.filter((x: string) => x !== id)
        : [...prev.menuIds, id],
    }));
  };

  const handleMapSelect = (pos: any) => {
    setForm((prev: any) => ({
      ...prev,
      latitude: pos.lat.toString(),
      longitude: pos.lng.toString(),
    }));
  };

  // ================= SUBMIT =================
  const submit = async (e: any) => {
    e.preventDefault();

    const name = form.name?.trim();
    if (!name) return toast.error("Company name required");

    try {
      await updateCompany({
        ...form,
        name,
      });

      toast.success("Company updated");
       window.location.href = "/companies";
    } catch (err: any) {
      toast.error(err?.response?.data?.message);
    }
  };

  if (!form) return <div className="p-6">Loading...</div>;

  return (
    <DashboardLayout>
      <div className="p-6 max-w-4xl mx-auto">

        <h2 className="text-xl font-semibold mb-4">
          Edit Company
        </h2>

        <form className="bg-white p-6 rounded shadow space-y-6" onSubmit={submit}>

          {/* BASIC */}
          <div className="grid md:grid-cols-2 gap-4">

            <Input label="Company Name *"
              value={form.name}
              onChange={(e:any)=>updateForm("name", e.target.value)} />

            <Input label="Email"
              value={form.contactEmail}
              onChange={(e:any)=>updateForm("contactEmail", e.target.value)} />

            <Input label="Phone"
              value={form.contactPhone}
              onChange={(e:any)=>updateForm("contactPhone", e.target.value)} />

            <Input label="Website"
              value={form.websiteUrl}
              onChange={(e:any)=>updateForm("websiteUrl", e.target.value)} />

            <Input label="Logo URL"
              value={form.logo}
              onChange={(e:any)=>updateForm("logo", e.target.value)} />

          </div>

          <Textarea label="Description"
            value={form.description}
            onChange={(e:any)=>updateForm("description", e.target.value)} />

          <Textarea label="Address"
            value={form.address}
            onChange={(e:any)=>updateForm("address", e.target.value)} />

          {/* MAP */}
          <div className="border-t pt-4">
            <label className="block text-sm font-medium mb-2">
              Location
            </label>

            <GoogleMapPickerScript
              onSelect={handleMapSelect}
              lat={form.latitude}
              lng={form.longitude}
            />

            <div className="grid grid-cols-2 gap-4 mt-3">
              <input value={form.latitude || ""} readOnly className="w-full border px-3 py-2 rounded bg-gray-100"/>
              <input value={form.longitude || ""} readOnly className="w-full border px-3 py-2 rounded bg-gray-100"/>
            </div>
          </div>

          {/* TYPES */}
          <div className="border-t pt-4">
            <label className="block text-sm font-medium mb-2">
              Company Types
            </label>

            <input
              placeholder="Search..."
              className="w-full border px-3 py-2 rounded mb-2"
              onChange={(e)=>setTypeSearch(e.target.value)}
            />

            <div className="border max-h-40 overflow-y-auto p-2">
              {companyTypes.map((t:any)=>(
                <label key={t.id} className="flex gap-2 mb-1">
                  <input
                    type="checkbox"
                    checked={form.companyTypeIds.includes(t.id)}
                    onChange={()=>toggleType(t.id)}
                  />
                  {t.name}
                </label>
              ))}
            </div>
          </div>

          {/* PUBLIC */}
          <div className="border-t pt-4">
            <label className="flex gap-2 items-center">
              <input
                type="checkbox"
                checked={form.isPublic}
                onChange={(e)=>updateForm("isPublic", e.target.checked)}
              />
              Public Company
            </label>
          </div>

          {/* MENUS */}
          <div className="border-t pt-4">
            <label className="block text-sm font-medium mb-2">
              Menu Access
            </label>

            <div className="border rounded max-h-60 overflow-y-auto p-2">
              {menus.map((m:any)=>(
                <div key={m.id} className="mb-2">

                  {/* PARENT */}
                  <label className="flex gap-2 font-medium">
                    <input
                      type="checkbox"
                      checked={form.menuIds.includes(m.id)}
                      onChange={()=>toggleMenu(m.id)}
                    />
                    {m.name}
                  </label>

                  {/* CHILDREN */}
                  {m.children?.map((c:any)=>(
                    <label key={c.id} className="flex gap-2 ml-5 text-sm">
                      <input
                        type="checkbox"
                        checked={form.menuIds.includes(c.id)}
                        onChange={()=>toggleMenu(c.id)}
                      />
                      {c.name}
                    </label>
                  ))}

                </div>
              ))}
            </div>
          </div>

          {/* ACTIONS */}
          <div className="flex justify-end gap-3 border-t pt-4">

            <button
              type="button"
              onClick={()=>navigate("/companies")}
              className="px-4 py-2 border rounded-lg"
            >
              Cancel
            </button>

            <button className="px-5 py-2 bg-blue-600 text-white rounded-lg">
              Update Company
            </button>

          </div>

        </form>
      </div>
    </DashboardLayout>
  );
};

const Input = ({ label, ...props }: any) => (
  <div>
    <label className="block text-sm font-medium mb-1">{label}</label>
    <input {...props} className="w-full border px-3 py-2 rounded"/>
  </div>
);

const Textarea = ({ label, ...props }: any) => (
  <div>
    <label className="block text-sm font-medium mb-1">{label}</label>
    <textarea rows={3} {...props} className="w-full border px-3 py-2 rounded"/>
  </div>
);

export default EditCompany;